import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {concatLatestFrom} from '@ngrx/operators';
import {Store, select} from '@ngrx/store';
import {tap} from 'rxjs/operators';
import {selectContextData} from '../context-data/context-data.selectors';
import {ContextData, contextDataToProductReference} from '../context-data';
import {
  selectCartItems,
  selectCartItemsForDisplay,
} from '../shopping-cart/cart.selectors';
import {EventEmitterService} from './event-emitter.service';
import {
  authenticate,
  cartLoad,
  cartUpdate,
  continueToCheckout,
  registerAnother,
} from './event.actions';
import {
  CartChangeEvent,
  CommercePurchaseMetadata,
  ContinueEvent,
} from './event.interface';
import {getCartTaxSuccess} from '../shopping-cart/cart.actions';
import {TaxEstimation} from '@sportngin/se-commerce-sdk';
import {CartItem, type ProductReference} from '../../data/model';

@Injectable()
export class EventEffects {
  registerAnother$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(registerAnother),
        concatLatestFrom(() => [
          this.store.pipe(select(selectCartItems)),
          this.store.pipe(select(selectCartItemsForDisplay)),
          this.store.pipe(select(selectContextData)),
        ]),
        tap(([__action, items, displayItems, context]) => {
          this.eventEmitterService.continue(
            this.buildContinueEvent(
              'registerAnother',
              items,
              displayItems,
              context,
            ),
          );
        }),
      );
    },
    {dispatch: false},
  );

  continueToCheckout$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(continueToCheckout),
        concatLatestFrom(() => [
          this.store.pipe(select(selectCartItems)),
          this.store.pipe(select(selectCartItemsForDisplay)),
          this.store.pipe(select(selectContextData)),
        ]),
        tap(([__action, items, displayItems, context]) => {
          this.router.navigate([
            {outlets: {commerceElement: 'redirect-to-checkout'}},
          ]);
          this.eventEmitterService.continue(
            this.buildContinueEvent('continue', items, displayItems, context),
          );
        }),
      );
    },
    {dispatch: false},
  );

  cartLoad$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(cartLoad),
        concatLatestFrom(() => [
          this.store.pipe(select(selectCartItems)),
          this.store.pipe(select(selectCartItemsForDisplay)),
          this.store.pipe(select(selectContextData)),
        ]),
        tap(([__action, items, displayItems, context]) => {
          const event = this.buildCartChangeEvent(items, displayItems, context);
          this.eventEmitterService.cartLoad(event);
        }),
      );
    },
    {dispatch: false},
  );

  cartUpdate$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(cartUpdate, getCartTaxSuccess),
        concatLatestFrom(() => [
          this.store.pipe(select(selectCartItems)),
          this.store.pipe(select(selectCartItemsForDisplay)),
          this.store.pipe(select(selectContextData)),
        ]),
        tap(([action, items, displayItems, context]) => {
          let tax: TaxEstimation | undefined = undefined;
          if ('tax' in action) {
            tax = action.tax;
          }
          const event = this.buildCartChangeEvent(
            items,
            displayItems,
            context,
            tax,
          );
          this.eventEmitterService.cartUpdate(event);
        }),
      );
    },
    {dispatch: false},
  );

  authenticate$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(authenticate),
        tap(props => {
          this.eventEmitterService.authenticate(props);
        }),
      );
    },
    {dispatch: false},
  );

  constructor(
    private actions$: Actions,
    private eventEmitterService: EventEmitterService,
    private router: Router,
    private store: Store,
  ) {}

  private buildCartChangeEvent(
    items: CartItem[],
    displayItems: CartItem[],
    context?: ContextData,
    tax?: TaxEstimation,
  ): CartChangeEvent {
    return new CartChangeEvent(items, tax, {
      displayItems,
      ...this.buildPurchaseMetadata(context),
    });
  }

  private buildContinueEvent(
    action: ContinueEvent['action'],
    items: CartItem[],
    displayItems: CartItem[],
    context?: ContextData,
    tax?: TaxEstimation,
  ): ContinueEvent {
    const cartChangeEvent = this.buildCartChangeEvent(
      items,
      displayItems,
      context,
      tax,
    );

    return {
      action,
      items: cartChangeEvent.items,
      displayItems: cartChangeEvent.displayItems,
      subtotal: cartChangeEvent.subtotal,
      ...this.buildPurchaseMetadata(context),
    };
  }

  private buildPurchaseMetadata(
    context?: ContextData,
  ): CommercePurchaseMetadata {
    if (!context) {
      return {};
    }

    const baseUriProxy = context.hostApplication?.baseUriProxy;
    const normalizedBaseUriProxy = baseUriProxy?.endsWith('/')
      ? baseUriProxy.slice(0, -1)
      : baseUriProxy;
    const references = this.buildReferences(context);

    return {
      guestKey: context.guestKey,
      organizationId: context.bossOrganization?.id,
      baseUriProxy: normalizedBaseUriProxy,
      references,
    };
  }

  private buildReferences(context: ContextData): ProductReference | undefined {
    if (!Array.isArray(context.signups) || context.signups.length === 0) {
      return undefined;
    }

    const references = contextDataToProductReference(context);
    if (
      !references.program &&
      !references.registration &&
      !references.participant
    ) {
      return undefined;
    }

    return references;
  }
}
